import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { CallLog, subscribeLogsForCallID } from '@adornis/voice-assistant/call-logs.js';
import { parseRecordingPlayback, subscribeRecordingPlayback } from '../../db/recording-playback.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import { renderCallLogList, formatCallLogsAsText } from './CallLogList.js';
import { CallFeedbackPanel } from './CallFeedbackPanel.js';
import { CallSuccessDetail } from './CallSuccessReview.js';
import { formatConversationWithDiagnostics, parseCallTranscript, renderCallTranscript } from './CallTranscript.js';
import { isDiagnosticLog } from './CallDiagnostics.js';
import { analyzeCall, callConversation, callFacts } from '../../db/call-analysis-resolvers.js';

/** The two views of the left column, in the order of the switch. Every call opens on the first. */
type CallLogView = 'gespraech' | 'logs';
const CALL_LOG_VIEWS: Array<[CallLogView, string]> = [
  ['gespraech', 'Gespräch'],
  ['logs', 'Logs'],
];
/** The call-log rows server/call-judge.ts buildTurns() builds the conversation from. Every other row is noise to it. */
const CONVERSATION_LOG_TYPES = new Set(['message', 'answer-generation-end']);

export const CallDetail = stateful(({ callID }: { callID: string }) => {
  // Subscribe to logs & recording for this call
  const logsSub = useQuery(
    () => (callID ? subscribeLogsForCallID(callID)(selectionSet(() => CallLog, 1)) : of([])),
    [callID],
  );
  const recordingSub = useQuery(() => (callID ? subscribeRecordingPlayback(callID)() : of(null)), [callID]);

  // 🆕 TASK 4 - the analysis starts on its own when this page opens. useQuery gives us the
  // mount-and-forget behaviour for free, and the resolver caches its answer on the CallSession
  // row, so revisiting the same call costs nothing and returns instantly.
  const analysisSub = useQuery(() => (callID ? analyzeCall(callID)() : Promise.resolve(null)), [callID]);
  const analysis = analysisSub.value as string | null | undefined;

  // 🆕 TASK 4 - the hard facts, read from our own collections rather than from the model.
  const factsSub = useQuery(() => (callID ? callFacts(callID)() : Promise.resolve(null)), [callID]);
  const factRows: [string, string][] = (() => {
    try {
      return Object.entries(JSON.parse(String(factsSub.value ?? '{}'))) as [string, string][];
    } catch {
      return [];
    }
  })();

  const logs = logsSub.value ?? [];
  const recording = parseRecordingPlayback(recordingSub.value);

  // 🆕 16/09/2026 - the conversation for the "Gespräch" view: the same turn list the Test Lab Call Judge shows, without
  // the judge (no model call, no findings). Switching views only re-renders, it never queries again.
  // It is loaded when the page opens, and again only when the conversation itself can have changed: when the live log
  // gains, loses or rewrites a row buildTurns reads. So a call that is still running, or ended moments ago (the
  // farewell's generation row lands ~15 s after end_call), does not stay stuck on an incomplete conversation while
  // "Logs" already shows the new rows. tts-chunk, VAD and every other row never query again.
  const conversationRows = logs.filter(l => CONVERSATION_LOG_TYPES.has(String(l.type)));
  const conversationSignature = logsSub.value
    ? `${conversationRows.length}:${conversationRows.reduce((n, l) => n + String(l.payload ?? '').length, 0)}`
    : undefined;
  // Per call (the [callID] deps start a new one): the log the page opened with, and the last answer. A holder, not
  // state: it is written during render and never triggers a render itself.
  const [conversationCache] = useState<{ openedWith?: string; raw?: unknown }>({}, [callID]);
  // The first log snapshot is what the opening query already read: it must not query a second time.
  if (conversationSignature !== undefined && conversationCache.openedWith === undefined)
    conversationCache.openedWith = conversationSignature;
  const conversationVersion =
    conversationSignature === undefined || conversationSignature === conversationCache.openedWith
      ? ''
      : conversationSignature;
  const conversationSub = useQuery(
    () => (callID ? callConversation(callID)() : Promise.resolve(null)),
    [callID, conversationVersion],
  );
  if (!conversationSub.loading && !conversationSub.error) conversationCache.raw = conversationSub.value;
  // While a newer conversation loads, the rows on screen stay (no loading flash, the panel keeps its scroll position).
  const turns = parseCallTranscript(conversationSub.loading ? conversationCache.raw : conversationSub.value);

  // 🆕 16/09/2026 - the left column shows the call as a conversation ("Gespräch", the default) or as the raw event log
  // ("Logs", unchanged). The view belongs to this page view: every call opens on "Gespräch", and the [callID] deps also
  // reset it when the page moves on to another call.
  const [view, setView] = useState<CallLogView>('gespraech', [callID]);
  const [copied, setCopied] = useState<boolean>(false);
  const selectView = (next: CallLogView) => {
    setView(next);
    if (copied) setCopied(false);
  };
  // Copies what the selected view shows: the conversation as plain text, or the log exactly as before.
  const copyView = async () => {
    await navigator.clipboard.writeText(
      view === 'logs' ? formatCallLogsAsText(logs) : formatConversationWithDiagnostics(turns, logs),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const renderConversation = () =>
    conversationSub.error
      ? html`<p class="text-error text-sm">Gespräch konnte nicht geladen werden: ${String(conversationSub.error)}</p>`
      : turns.length || logs.some(isDiagnosticLog)
        ? renderCallTranscript(turns, { diagnosticLogs: logs })
        : conversationSub.loading
          ? html`<div class="flex items-center gap-3 text-base-content/60">
              <span class="loading loading-dots loading-md"></span>
              <span class="text-sm">Gespräch wird geladen …</span>
            </div>`
          : html`<p class="text-base-content/50 italic">Keine Gesprächsdaten für diesen Anruf.</p>`;

  return html`<section class="container mx-auto py-8 px-4">
    <div class="card">
      <div class="card-body space-y-6">
        <button class="btn btn-ghost" @click=${() => window.history.back()}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="inline w-5 h-5 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Zurück
        </button>
        <h2 class="card-title text-2xl font-bold text-primary">Call Details</h2>

        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
          <div class="space-y-6 min-w-0">
            <div>
              <h3 class="font-semibold mb-2">Aufzeichnung</h3>
              ${recording.url
                ? html`<audio controls src="${recording.url}" class="w-full"></audio>`
                : html`<p class="text-base-content/60 italic">
                    ${recording.state === 'expired'
                      ? 'Aufzeichnung nach 21 Tagen gelöscht. Das Gesprächsprotokoll bleibt verfügbar.'
                      : 'Keine Aufzeichnung verfügbar.'}
                  </p>`}
            </div>

            <div class="w-full">
              <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 class="font-semibold">Call Log</h3>
                <div class="flex flex-wrap items-center gap-2">
                  <div class="join" role="group" aria-label="Ansicht">
                    ${CALL_LOG_VIEWS.map(
                      ([id, label]) =>
                        html`<button
                          type="button"
                          class="btn btn-xs join-item ${view === id ? 'btn-neutral' : 'btn-outline'}"
                          aria-pressed=${view === id ? 'true' : 'false'}
                          @click=${() => selectView(id)}
                        >
                          ${label}
                        </button>`,
                    )}
                  </div>
                  <button
                    class="btn btn-xs btn-outline"
                    ?disabled=${view === 'gespraech' && !turns.length && !logs.some(isDiagnosticLog)}
                    @click=${copyView}
                  >
                    ${copied ? '✓ Kopiert!' : view === 'logs' ? '📋 Logs kopieren' : '📋 Gespräch kopieren'}
                  </button>
                </div>
              </div>
              <div class="bg-base-200 rounded-lg p-4 min-h-112 max-h-[75vh] overflow-y-auto">
                ${view === 'logs' ? renderCallLogList(logs) : renderConversation()}
              </div>
            </div>
          </div>

          <div class="min-w-0">
            <h3 class="font-semibold mb-2">Analysis</h3>
            <div class="bg-base-200 rounded-lg p-5 min-h-112">
              ${analysisSub.error
                ? html`<p class="text-error text-sm">Analyse fehlgeschlagen: ${String(analysisSub.error)}</p>`
                : analysis
                  ? html`<p class="whitespace-pre-wrap text-sm leading-relaxed">${analysis}</p>`
                  : html`<div class="flex items-center gap-3 text-base-content/60">
                      <span class="loading loading-dots loading-md"></span>
                      <span class="text-sm">Analyse läuft …</span>
                    </div>`}
            </div>

            ${factRows.length
              ? html`<div class="mt-6">
                  <h3 class="font-semibold mb-2">Daten</h3>
                  <div class="bg-base-200 rounded-lg p-5">
                    <dl class="grid grid-cols-[11rem_1fr] gap-x-4 gap-y-1 text-sm">
                      ${factRows.map(
                        ([k, v]) =>
                          html`<dt class="text-base-content/60">${k}</dt>
                            <dd class="font-medium break-all">${v}</dd>`,
                      )}
                    </dl>
                  </div>
                </div>`
              : nothing}
            ${callID ? html`<div class="mt-6">${CallSuccessDetail({ callID })}</div>` : nothing}
            ${callID ? html`<div class="mt-6">${CallFeedbackPanel({ callID })}</div>` : nothing}
          </div>
        </div>
      </div>
    </div>
  </section>`;
});
